import { css } from '@emotion/css';
import { Button, StatusMessage } from 'is24-corecss';
import React, { ReactNode } from 'react';
import { OverviewDescription } from './OverviewDescription';
import Notifications from './Notifications';
import { List } from './List';
import { OverviewFooter } from './OverviewFooter';
import { APP_CONFIG } from '@/config';
import { LEGACY_BILLCHECK_STATUS, ILegacyBillcheckStatus } from '@/@types/legacyBillCheck';
import { useIntl } from '@/i18n/hooks/useIntl';
import { default as Msg } from '@/i18n/components/FormattedMessage';

const styles = {
  statusMessageParagraph: css`
    margin-top: 8px;
  `,
  statusMessageOrdered: { paddingTop: '12px' },
  statusMessageCta: { marginTop: '12px' },
};

export const BillCheckLegacyOverviewTab = ({
  isEntitled,
  lastBillCheck,
}: {
  isEntitled: boolean;
  lastBillCheck: ILegacyBillcheckStatus;
}) => {
  const { t } = useIntl();

  if (lastBillCheck.status === LEGACY_BILLCHECK_STATUS.ORDERED && !!lastBillCheck?.orderCartId) {
    const minekoRedirectionURL = `${APP_CONFIG.is24MinekoURL}/checkout?orderCartId=${lastBillCheck.orderCartId}`;

    return (
      <>
        <StatusMessage
          headline={t({
            id: 'benefits.billCheckV3.modal.overview.statusBox.ordered.headline',
            defaultMessage: 'Sie haben eine unvollständige Bewerbung.',
          })}
          statusType={'confirm'}
          message={
            <div style={styles.statusMessageOrdered}>
              {t({
                id: 'benefits.billCheckV3.modal.overview.statusBox.ordered.description',
                defaultMessage:
                  'Bitte setzen Sie Ihre Registrierung auf der Mineko-Website fort, um Ihren Vorgang abzuschließen.',
              })}
              <Button
                appearance='primary'
                onClick={() => window.open(minekoRedirectionURL, '_blank')}
                style={styles.statusMessageCta}
              >
                {t({
                  id: 'benefits.billCheckV3.modal.overview.statusBox.ctaLabel',
                  defaultMessage: 'Fahren Sie mit der Registrierung fort',
                })}
              </Button>
            </div>
          }
        />
        <OverviewFooter />
      </>
    );
  }

  if (lastBillCheck.status === LEGACY_BILLCHECK_STATUS.ACTION_REQUIRED) {
    return (
      <>
        <Notifications />
        <List />
        <OverviewFooter />
      </>
    );
  }

  if (lastBillCheck?.status === LEGACY_BILLCHECK_STATUS.SUBMITTED && !!lastBillCheck?.minekoTicketId) {
    const minekoRedirectionURL = `${APP_CONFIG.minekoURL}/kundenbereich/bestellung/uebersicht/${lastBillCheck.minekoTicketId}/`;
    return (
      <>
        <StatusMessage
          statusType='confirm'
          headline={t({
            id: `benefits.billCheckV3.modal.overview.statusBox.submitted.headline`,
            defaultMessage: 'Die Prüfung deiner Unterlagen hat begonnen',
          })}
          message={
            <>
              <p className={styles.statusMessageParagraph}>
                {t({
                  id: `benefits.billCheckV3.modal.overview.statusBox.submitted.description`,
                  defaultMessage: 'Innerhalb von 5 Werktagen erhältst du das Ergebnis per E-Mail.',
                })}
              </p>
              <Msg
                id='benefits.billCheckV3.modal.overview.statusBox.ticketNumberInfoLabel'
                defaultMessage='Deine Auftragsnummer: '
                values={{
                  b: (children: ReactNode) => <b>{children}</b>,
                }}
              />
              <a href={minekoRedirectionURL} target='_blank'>
                {lastBillCheck.minekoTicketId}
              </a>
              <Button
                appearance='primary'
                onClick={() => window.open(minekoRedirectionURL, '_blank')}
                style={styles.statusMessageCta}
              >
                {t({
                  id: 'benefits.billCheckV3.modal.overview.statusBox.ctaLabel',
                  defaultMessage: 'Fahren Sie mit der Registrierung fort',
                })}
              </Button>
            </>
          }
        />
        <List />
        <OverviewFooter />
      </>
    );
  }

  if (lastBillCheck.status === LEGACY_BILLCHECK_STATUS.DONE) {
    return (
      <>
        <StatusMessage
          statusType='confirm'
          headline={t({
            id: `benefits.billCheckV3.modal.overview.statusBox.done.headline`,
            defaultMessage: 'Die Prüfung deiner Unterlagen wurde abgeschlossen',
          })}
          message={
            <>
              <p className={styles.statusMessageParagraph}>
                {t({
                  id: `benefits.billCheckV3.modal.overview.statusBox.done.description`,
                  defaultMessage: 'Wir haben dir das Ergebnis der Prüfung per E-Mail zugesendet.',
                })}
              </p>
              <Msg
                id='benefits.billCheckV3.modal.overview.statusBox.ticketNumberInfo'
                defaultMessage='Deine Auftragsnummer: {minekoTicketId}'
                values={{
                  minekoTicketId: lastBillCheck.minekoTicketId,
                  b: (children: ReactNode) => <b>{children}</b>,
                }}
              />
            </>
          }
        />
        <List />
        <OverviewFooter />
      </>
    );
  }

  return (
    <>
      <OverviewDescription isEntitled={isEntitled} />
      <List />
    </>
  );
};
